import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Calendar, Save, Plus, X, Clock } from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TourAvailability = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [settings, setSettings] = useState({
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    start_hour: 10,
    end_hour: 17,
    max_per_day: 4,
    default_slots: ["Wednesday 11am", "Thursday 2pm"],
  });
  const [newSlot, setNewSlot] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/tour-availability`, { headers });
      // Functional update to avoid capturing `settings` in the closure —
      // server values overlay any defaults already in state.
      setSettings((prev) => ({ ...prev, ...r.data }));
    } catch (err) {
      console.error("tour-availability fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleDay = (day) => {
    setSettings((s) => ({
      ...s,
      days: s.days.includes(day) ? s.days.filter((d) => d !== day) : [...s.days, day],
    }));
  };

  const addSlot = () => {
    const trimmed = newSlot.trim();
    if (!trimmed) return;
    if (settings.default_slots.includes(trimmed)) {
      toast({ title: "Slot already added", variant: "destructive" });
      return;
    }
    setSettings((s) => ({ ...s, default_slots: [...s.default_slots, trimmed] }));
    setNewSlot("");
  };

  const removeSlot = (slot) => {
    setSettings((s) => ({ ...s, default_slots: s.default_slots.filter((x) => x !== slot) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tour-availability`, settings, { headers });
      toast({ title: "Saved", description: "Tour availability updated." });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err.response?.data?.detail || "Try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="tour-availability-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="tour-availability-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1F2A44] font-['Playfair_Display']">
            Tour Availability
          </h1>
          <p className="text-slate-600 text-sm">
            When Bryan can run tours. Used in D3 drip emails and tour-assign flow.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="tour-availability-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#DE6E4B] text-white rounded-lg font-bold hover:bg-[#c75f3f] disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Available days */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="available-days-card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-[#DE6E4B]" />
          <h3 className="font-bold text-[#1F2A44]">Available days</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button
              key={d}
              onClick={() => toggleDay(d)}
              data-testid={`day-${d.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition ${
                settings.days.includes(d)
                  ? "border-[#DE6E4B] bg-[#DE6E4B] text-white"
                  : "border-slate-200 text-slate-700 hover:border-[#1F2A44] bg-white"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Hours */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="hours-card">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#DE6E4B]" />
          <h3 className="font-bold text-[#1F2A44]">Hours & capacity</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-500">Start (hour)</span>
            <input
              type="number" min="6" max="20"
              value={settings.start_hour}
              onChange={(e) => setSettings({ ...settings, start_hour: Number(e.target.value) })}
              className="mt-1 w-full border-2 border-slate-200 rounded-lg px-3 py-2"
              data-testid="start-hour"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-500">End (hour)</span>
            <input
              type="number" min="7" max="22"
              value={settings.end_hour}
              onChange={(e) => setSettings({ ...settings, end_hour: Number(e.target.value) })}
              className="mt-1 w-full border-2 border-slate-200 rounded-lg px-3 py-2"
              data-testid="end-hour"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-slate-500">Max tours/day</span>
            <input
              type="number" min="1" max="20"
              value={settings.max_per_day}
              onChange={(e) => setSettings({ ...settings, max_per_day: Number(e.target.value) })}
              className="mt-1 w-full border-2 border-slate-200 rounded-lg px-3 py-2"
              data-testid="max-per-day"
            />
          </label>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Example: Mon–Fri, 10am–5pm, max 4 tours/day. Times shown in your local zone.
        </p>
      </div>

      {/* Default slots for D3 drip */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="default-slots-card">
        <h3 className="font-bold text-[#1F2A44] mb-2">Default tour slots offered in D3 drip</h3>
        <p className="text-xs text-slate-500 mb-3">
          These appear in your "2 tour slots left this week" email. Add 1–4 options.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {settings.default_slots.map((slot) => (
            <span
              key={slot}
              data-testid={`slot-pill-${slot.replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-2 pl-3 pr-1 py-1 bg-[#1F2A44] text-white rounded-full text-sm"
            >
              {slot}
              <button
                onClick={() => removeSlot(slot)}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                data-testid={`slot-remove-${slot.replace(/\s+/g, "-")}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {settings.default_slots.length === 0 && (
            <span className="text-sm text-slate-400">No default slots — add one below.</span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSlot}
            onChange={(e) => setNewSlot(e.target.value)}
            placeholder='e.g. "Wednesday 11am"'
            className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-sm"
            data-testid="new-slot-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSlot(); } }}
          />
          <button
            onClick={addSlot}
            className="inline-flex items-center gap-1 px-4 py-2 bg-[#1F2A44] text-white rounded-lg font-bold hover:bg-[#2a3a5e]"
            data-testid="add-slot-button"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourAvailability;
